import React, { useState } from 'react';
import { Button, Select } from '../../../design-system';
import { listAllProjectSamples } from '../../../api/data';
import { LambdaMethod, Sample } from '../../../@types/samples/types';
import { Well } from '../../../@types/wells/types';
import {
  AnalysisMethod,
  ANALYSIS_METHODS,
  KR_RUN_MODE_LABELS,
  KrRunMode,
  METHOD_LABELS,
  isKrMethod,
} from '../types';
import { LAMBDA_METHODS } from '../../../components/dashboard/components/LambdaAnalysis/methodConstants';
import styles from '../MultiAnalysisPage.module.css';

/** Sentinel value for the "All samples" dropdown option. */
const ALL_SAMPLES = '__all__';

type Props = {
  projectUid: string | null;
  wells: Well[];
  sampleCount: number;
  /** Active batch methodology (Liao / Brooks-Corey). */
  method: AnalysisMethod;
  onMethodChange: (method: AnalysisMethod) => void;
  /** Brooks-Corey solver applied to the whole batch (shown for lambda only). */
  lambdaMethod: LambdaMethod;
  onLambdaMethodChange: (method: LambdaMethod) => void;
  /** Opt-in: run all four solvers per sample and auto-pick the best fit. */
  compareAllMethods: boolean;
  onCompareAllMethodsChange: (value: boolean) => void;
  /** Kr batch run mode (shown for water-oil / gas-oil only). */
  krRunMode: KrRunMode;
  onKrRunModeChange: (mode: KrRunMode) => void;
  onOpenModal: () => void;
  /** Replace the current selection with the chosen group (well / all samples). */
  onLoadGroup: (samples: Sample[]) => void;
  onClear: () => void;
  disabled?: boolean;
};

/**
 * Sample-selection toolbar: manual picking via the shared LoadSampleModal,
 * plus a dropdown that loads a whole group (a well, or all samples in the
 * project). Choosing a group REPLACES the current selection. The analysis
 * (category) view filter lives in the bottom controls bar.
 *
 * The methodology selector (Liao / Brooks-Corey) picks which solver the
 * batch runs; each methodology keeps its own preview results, so switching
 * swaps the grid to that methodology's batch without losing the other's.
 */
const SelectionToolbar = ({
  projectUid,
  wells,
  sampleCount,
  method,
  onMethodChange,
  lambdaMethod,
  onLambdaMethodChange,
  compareAllMethods,
  onCompareAllMethodsChange,
  krRunMode,
  onKrRunModeChange,
  onOpenModal,
  onLoadGroup,
  onClear,
  disabled,
}: Props) => {
  const [loading, setLoading] = useState(false);
  // The chosen group, kept so its name shows in the dropdown after selection.
  const [selectedGroup, setSelectedGroup] = useState('');

  const options = [
    { value: ALL_SAMPLES, label: 'All samples' },
    ...wells.map(well => ({
      value: well.uid,
      label: well.well_name ?? well.uid,
    })),
  ];

  // Solver-method options: reuse the shared solver metadata (labels, summaries
  // and the "recommended" flag) so the batch selector matches the single-sample
  // panel. The summary below the row explains the selected method.
  const lambdaMethodOptions = LAMBDA_METHODS.map(info => ({
    value: info.value,
    label: info.recommended ? (
      <span className={styles.methodOption}>
        {info.label}
        <span className={styles.methodRecommendedBadge}>recommended</span>
      </span>
    ) : (
      info.label
    ),
  }));
  const lambdaMethodSummary = LAMBDA_METHODS.find(
    info => info.value === lambdaMethod
  )?.summary;

  const handleGroupChange = async (value: string) => {
    if (!projectUid || !value) return;
    setSelectedGroup(value);
    setLoading(true);
    try {
      const samples = await listAllProjectSamples(projectUid);
      const list = Array.isArray(samples) ? samples : [];
      onLoadGroup(
        value === ALL_SAMPLES
          ? list
          : list.filter(sample => sample.well_uid === value)
      );
    } catch {
      // Leave the selection unchanged on error; the empty state remains.
    } finally {
      setLoading(false);
    }
  };

  const handleClear = () => {
    setSelectedGroup('');
    onClear();
  };

  return (
    <>
      <div className={styles.toolbar}>
        <div className={styles.toolbarMain}>
          <Button
            variant="primary"
            size="sm"
            onClick={onOpenModal}
            disabled={disabled}
          >
            Select samples…
          </Button>

          <div className={styles.toolbarWellPicker}>
            <Select
              size="sm"
              fullWidth
              placeholder="Load a well or all samples"
              // Controlled with the chosen group so its name stays visible. The
              // design-system Select still fires onChange on every pick, so
              // re-selecting the same group reloads it.
              value={selectedGroup}
              onChange={handleGroupChange}
              disabled={disabled || loading}
              options={options}
            />
          </div>

          <span className={styles.toolbarCount}>
            {sampleCount} sample{sampleCount === 1 ? '' : 's'} selected
          </span>

          {sampleCount > 0 ? (
            <Button
              variant="ghost"
              size="sm"
              onClick={handleClear}
              disabled={disabled}
            >
              Clear
            </Button>
          ) : null}

          {method === 'lambda' ? (
            <div
              className={styles.solverCluster}
              role="group"
              aria-label="Brooks-Corey solver method"
            >
              <span className={styles.toolbarCount}>Solver method:</span>
              {compareAllMethods ? (
                <span className={styles.solverAuto}>
                  Auto — best per sample
                </span>
              ) : (
                <div className={styles.solverSelect}>
                  <Select
                    size="sm"
                    fullWidth
                    value={lambdaMethod}
                    onChange={value =>
                      onLambdaMethodChange(value as LambdaMethod)
                    }
                    disabled={disabled}
                    options={lambdaMethodOptions}
                  />
                </div>
              )}
              <label
                className={styles.compareToggle}
                title="Fit all four solvers for every sample and automatically pick the best-fitting one (about 4× slower to preview)."
              >
                <input
                  type="checkbox"
                  checked={compareAllMethods}
                  onChange={event =>
                    onCompareAllMethodsChange(event.target.checked)
                  }
                  disabled={disabled}
                />
                Compare all methods
              </label>
            </div>
          ) : null}

          {isKrMethod(method) ? (
            <div
              className={styles.solverCluster}
              role="group"
              aria-label="Kr run mode"
            >
              <span className={styles.toolbarCount}>Run mode:</span>
              <div className={styles.solverSelect}>
                <Select
                  size="sm"
                  fullWidth
                  value={krRunMode}
                  onChange={value => onKrRunModeChange(value as KrRunMode)}
                  disabled={disabled}
                  options={(
                    Object.keys(KR_RUN_MODE_LABELS) as KrRunMode[]
                  ).map(mode => ({
                    value: mode,
                    label: KR_RUN_MODE_LABELS[mode],
                  }))}
                />
              </div>
            </div>
          ) : null}
        </div>

        <div className={styles.methodToggle} aria-label="Analysis methodology">
          <span className={styles.toolbarCount}>Analysis:</span>
          {ANALYSIS_METHODS.map(value => (
            <Button
              key={value}
              variant={method === value ? 'primary' : 'secondary'}
              size="sm"
              aria-pressed={method === value}
              onClick={() => onMethodChange(value)}
              disabled={disabled}
            >
              {METHOD_LABELS[value]}
            </Button>
          ))}
        </div>
      </div>

      {method === 'lambda' ? (
        <p className={styles.solverSummary}>
          {compareAllMethods
            ? 'Fits all four solvers for each sample and auto-selects the lowest-error one. Open a sample to compare and override the choice.'
            : lambdaMethodSummary}
        </p>
      ) : null}

      {isKrMethod(method) ? (
        <p className={styles.solverSummary}>
          {krRunMode === 'auto'
            ? 'Runs the complete Kr workflow per sample: lab Corey analysis plus automated refinement (true Sor, endpoint extrapolation, refined No/Nw). Samples with manual refinement are preserved until you confirm the overwrite on save.'
            : 'Runs only the lab baseline Corey analysis (normalized curves and mean exponents), without the refinement steps.'}
        </p>
      ) : null}
    </>
  );
};

export default SelectionToolbar;
